import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import styled from 'styled-components';
import VerifierCompaniesList from 'components/v2/compiler/panels/third-party-verification/epd-verifier-selector/VerifierCompaniesList';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { GLOBAL_OPTION } from 'constants/constants';
import { useVerifierMarketplaceCountriesLookupWithGlobal } from 'hooks/lookups';
import CompanyService from 'services/CompanyService';
import { VerificationType } from 'services/EpdClient';
import { AddVerifierToCompanyModel } from 'types/dynamic-forms/companies/AddVerifierToCompanyModel';
import { EPDVerifierCompanyModel, Option, VerifierType } from 'types/types';
import useDialogListQuery, { dialogListQueryOptions } from '../../hooks/useDialogListQuery';

const VerifierFilterOption = {
  All: 'All',
  ApprovedIndividual: 'Approved Individual Verifiers',
  CertificationBodies: 'Accredited Certification Bodies',
};

type TProps = {
  onHide(): void;
  isOpened: boolean;
  /** Whether the dialog can be opened at all, so its list is worth loading before it opens. */
  canPrefetch: boolean;
  companyId: string;
  onAddVerifier(model: AddVerifierToCompanyModel): Promise<void>;
};

const fetchVerifierCompanies = (companyId: string, country: Option | undefined, verifierFilter: string) => {
  const isGlobalMarketplace = country?.label === GLOBAL_OPTION.label ? true : undefined;

  switch (verifierFilter) {
    case VerifierFilterOption.ApprovedIndividual:
      return CompanyService.getCompanyIndividualVerifiers(
        companyId,
        country?.value,
        isGlobalMarketplace,
        undefined,
        VerificationType.EPDVerification
      );
    case VerifierFilterOption.CertificationBodies:
      return CompanyService.getCompanyCertificationBodies(
        companyId,
        country?.value,
        isGlobalMarketplace,
        undefined,
        VerificationType.EPDVerification
      );
    default:
      return CompanyService.getCompanyVerifiers(
        companyId,
        country?.value,
        isGlobalMarketplace,
        undefined,
        VerificationType.EPDVerification
      );
  }
};

const verifiersQueryOptions = (companyId: string, country: Option | undefined, verifierFilter: string) =>
  dialogListQueryOptions<EPDVerifierCompanyModel>(
    ['DF_VERIFIERS', companyId, country?.value ?? null, country?.label === GLOBAL_OPTION.label, verifierFilter],
    () => fetchVerifierCompanies(companyId, country, verifierFilter)
  );

const VerifierSelectInputRendererDialog = ({ onHide, isOpened, canPrefetch, companyId, onAddVerifier }: TProps) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Kept apart from the cached list, so a search does not lose it; cleared when the dialog closes.
  const [errorVerifierId, setErrorVerifierId] = useState<string | undefined>();
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const [selectedVerifier, setSelectedVerifier] = useState<EPDVerifierCompanyModel | undefined>();
  const verifierTypeOptions = [
    { value: VerifierFilterOption.All, label: VerifierFilterOption.All },
    { value: VerifierFilterOption.ApprovedIndividual, label: VerifierFilterOption.ApprovedIndividual },
    { value: VerifierFilterOption.CertificationBodies, label: VerifierFilterOption.CertificationBodies },
  ] as Option[];
  const [areButtonsDisabled, setAreButtonsDisabled] = useState<boolean>(false);

  const [selectedVerifierType, setSelectedVerifierType] = useState<Option>(verifierTypeOptions[0]);

  // The unfiltered list the dialog opens with is loaded as soon as the form is on screen, so it is usually ready by then.
  useEffect(() => {
    if (canPrefetch) {
      void queryClient.prefetchQuery(verifiersQueryOptions(companyId, undefined, VerifierFilterOption.All));
    }
  }, [canPrefetch, companyId, queryClient]);

  // Not prefetched: other screens share this cache entry with another shape of query.
  const countries = useVerifierMarketplaceCountriesLookupWithGlobal({ enabled: isOpened }).data || [];

  const verifiersQuery = useDialogListQuery(
    verifiersQueryOptions(companyId, selectedCountry, selectedVerifierType.value),
    isOpened
  );
  const verifiers = useMemo(() => verifiersQuery.data ?? [], [verifiersQuery.data]);

  const filteredVerifierCompanies = useMemo(() => {
    const matching = searchTerm
      ? verifiers.filter(
          (item) => item.name?.toLowerCase().includes(searchTerm) || item.email?.toLowerCase().includes(searchTerm)
        )
      : verifiers;
    return matching.map((c) => (c.id === errorVerifierId ? { ...c, errorText: 'Something went wrong.' } : c));
  }, [verifiers, searchTerm, errorVerifierId]);

  const onChangeVerifierCountry = async (propertyName: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const onChangeVerifierType = async (propertyName: string, val: any) => {
    setSelectedVerifierType(verifierTypeOptions.find((v) => v.value === val)!);
  };

  const handleAddVerifier = async (verifierId: string) => {
    setAreButtonsDisabled(true);

    try {
      const verifier = verifiers.find((v) => v.id === verifierId);
      if (!verifier) {
        throw new Error('Verifier is selected but not found in data set');
      }

      const connectVerifierModel: AddVerifierToCompanyModel = {
        certificationBodyCompanyId: verifier.verifierType === VerifierType.Body ? verifierId : null,
        individualVerifierMembershipId: verifier.verifierType === VerifierType.Individual ? verifierId : null,
      };
      await onAddVerifier(connectVerifierModel);
      toaster({
        severity: 'success',
        summary: 'The verifier was added to the EPD!',
        details: 'Now the EPD Verifier has an access to the EPD.',
      });
      onHide();
    } catch {
      setErrorVerifierId(selectedVerifier?.id);
    } finally {
      setAreButtonsDisabled(false);
    }
  };

  const handleSearchVerifierCompanies = (event: any) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  return (
    <StyledDialog
      resizable={false}
      header="EPD Verifier"
      visible={isOpened}
      contentStyle={!!filteredVerifierCompanies.length ? {} : { overflowY: 'initial' }}
      onHide={() => {
        setSelectedVerifier(undefined);
        setSelectedCountry(undefined);
        setSelectedVerifierType(verifierTypeOptions[0]);
        setSearchTerm('');
        setErrorVerifierId(undefined);
        onHide();
      }}
    >
      <DialogContentContainer>
        <HelpBox>
          To get in touch with one or several verifiers please click on the button “Request collaboration”. An e-mail will be
          sent to the verifier and they will reach out to respond to your query. Once you will get the verifier’s
          collaboration response, you can “Add verifier” to your EPD.
        </HelpBox>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Country"
            name="verifierCountry"
            options={countries}
            value={selectedCountry}
            onChanged={onChangeVerifierCountry}
            placeholder="Select"
            isClearable={true}
          />
        </DialogContentRow>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Verifier type"
            name="verifierType"
            options={verifierTypeOptions}
            value={selectedVerifierType}
            onChanged={onChangeVerifierType}
            placeholder="Select"
            isClearable={false}
          />
        </DialogContentRow>
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Verifier name or email"
            tooltipPosition={'right'}
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchVerifierCompanies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        {verifiersQuery.isPending && (
          <LoadingContainer>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} aria-label="Loading verifiers" />
          </LoadingContainer>
        )}
        <VerifierCompaniesList
          epdVerifier={null}
          verifierCompanies={filteredVerifierCompanies}
          selectedVerifierCompany={selectedVerifier}
          onSelectVerifierCompany={setSelectedVerifier}
          onAddVerifier={handleAddVerifier}
          buttonsAreDisabled={areButtonsDisabled}
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  width: 35rem;

  .p-dialog-header {
    position: relative;
    z-index: 1;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.04);
  }

  .p-dialog-header-close svg path {
    fill: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
  scrollbar-gutter: stable;
`;

const DialogContentRow = styled.div`
  margin-top: 25px;
  margin-bottom: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

export default VerifierSelectInputRendererDialog;
